// packages/core/src/learning/schools/dividend-investing.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. (Worldview named no figures; Lowell
// Miller appears as a canon author only.)
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const DIVIDEND_INVESTING_EXHIBIT: SchoolExhibitEntry = {
	id: 'dividend-investing',
	tagline:
		'Dividend investing believes cash sent back to owners can keep you disciplined.',
	worldviewSummary:
		'Dividend investing is paying attention to businesses that return cash to shareholders instead of only promising future growth. This lens protects you from treating investing like a scoreboard where price movement is the only thing that matters. It accepts tradeoffs: dividends can create income preference, tax drag, sector concentration, and missed growth. The blind spot is that a dividend can make a weak business look safer than it really is.',
	coreBeliefs: [
		'Dividend investing believes cash payments can make ownership feel more concrete.',
		'Dividend investing believes shareholder discipline matters.',
		'Dividend investing believes income can help investors stay patient during ugly markets.',
		'Dividend investing believes a payout is only healthy when the business can support it.',
		'Dividend investing believes yield should be questioned, not worshiped.',
		'Dividend investing believes total return still matters, even when income feels good.',
	],
	warnsAgainst: [
		'Chasing high yield without asking why the yield is high.',
		'Confusing a dividend check with a strong business.',
		'Ignoring taxes, concentration, and slower growth.',
		'Thinking income matters so much that price, quality, and risk stop mattering.',
	],
	helps: 'This lens helps people who value visible income, patience, and business discipline. It may not fit someone who wants maximum simplicity, broad market exposure, or a strategy focused mainly on long term growth.',
	figures: [],
	canon: [
		{
			title: 'Dividend discount model',
			oneLineIdea:
				'A stock can be viewed through the cash it may return to owners over time.',
		},
		{
			title: 'Dividend aristocrats',
			oneLineIdea:
				'A long record of dividend increases can signal discipline, but it does not remove business risk.',
		},
		{
			title: 'The Single Best Investment',
			author: 'Lowell Miller',
			oneLineIdea:
				'The useful lens is quality income, not chasing the biggest payout.',
		},
		{
			title: 'Payout ratio',
			oneLineIdea:
				'A dividend only deserves trust when the business is not overpaying beyond what it can support.',
		},
		{
			title: 'Total return thinking',
			oneLineIdea:
				'Income and price change both matter because the investor lives with the whole result.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-dividend-investing',
	debateIds: [],
	applyActions: [
		{
			label: 'Compare income preference against your current money picture',
			action: {
				type: 'openDashboard',
				label: 'Check your money picture',
			},
		},
		{
			label: 'Test how steady monthly income changes your freedom path',
			action: {
				type: 'openSimulator',
				label: 'Simulate steady income',
				scenarioType: 'custom',
			},
		},
		{
			label: 'See whether more surplus changes your savings rate',
			action: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
		},
		{
			label: 'Write your rules for avoiding yield chasing',
			action: {
				type: 'noToolAction',
				label: 'Write your yield rules',
				prompt: 'Write three warning signs you would check before trusting an income story.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Dividend investing is not about chasing checks. It is about trusting the business behind them.',
			kind: 'hook',
		},
		{
			line: 'A big yield can be a reward, or it can be a warning label.',
			kind: 'hook',
		},
		{
			line: 'What did dividend income teach you that price charts did not?',
			kind: 'interview-question',
		},
		{
			line: 'When does chasing income become more dangerous than waiting for growth?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps users test whether income actually changes their options.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'The dividend lens makes investing feel less abstract, without pretending checks erase risk.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show two portfolios with the same value, then compare how income changes the owner’s emotions.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask someone if they want cash flow, total return, or the feeling of control.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
